import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, Heart, ChevronLeft, CheckCircle2, Clock, Users } from "lucide-react";
import { useKioskIdleReturn } from "@/lib/useKioskIdleReturn";
import { formatDisplayName } from "@/lib/names";

// thedisciple.prayer — public entry.
// Members walk up to a tablet (kiosk) or scan the org's prayer QR on their phone,
// find themselves in the directory, confirm with last-4, tap "Check in for prayer".
// One record per tap. No timer, no checkout.
//
// The same page also serves as a "kiosk" — after each check-in it clears the
// personal state and returns to the search screen automatically.

const relTime = (iso) => {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  const secs = Math.max(0, Math.round((Date.now() - then) / 1000));
  if (secs < 60) return `${secs}s ago`;
  const mins = Math.round(secs / 60);
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.round(hrs / 24)}d ago`;
};

const formatDate = (iso) => new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });

function OrgHeader({ orgName }) {
  return (
    <div className="text-center mb-6">
      <div className="overline mb-2" style={{ color: "var(--terracotta)", fontSize: "0.65rem" }}>thedisciple.prayer</div>
      <h1 className="font-serif-display text-4xl sm:text-5xl leading-none" style={{ color: "var(--ink)" }}>Prayer</h1>
      {orgName && <p className="mt-3 text-sm" style={{ color: "var(--muted)" }}>{orgName}</p>}
    </div>
  );
}

function RecentPanel({ code, refreshTick }) {
  const [items, setItems] = useState([]);
  const load = useCallback(() => {
    api.get(`/public/prayer/${code}/recent`).then((r) => setItems(r.data.recent || [])).catch(() => {});
  }, [code]);
  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);
  // Parent bumps `refreshTick` after a successful check-in so the panel updates immediately.
  useEffect(() => { if (refreshTick > 0) load(); }, [refreshTick, load]);
  return (
    <div className="mt-8 p-4 rounded-md" style={{ background: "var(--bg-alt)", border: "1px solid var(--border-subtle)" }} data-testid="prayer-recent">
      <div className="flex items-center gap-2 mb-2">
        <Clock size={13} style={{ color: "var(--muted)" }} />
        <span className="text-[0.6rem] uppercase tracking-[0.18em]" style={{ color: "var(--muted)" }}>Who&rsquo;s prayed with me (last 24h)</span>
      </div>
      {items.length === 0 ? (
        <p className="text-xs italic" style={{ color: "var(--muted)" }}>Be the first today.</p>
      ) : (
        <ul className="space-y-2">
          {items.slice(0, 8).map((it, i) => (
            <li key={i} className="text-sm" style={{ color: "var(--ink-2)" }}>
              <div className="flex items-center justify-between">
                <span>{it.display_name}</span>
                <span className="text-[0.65rem]" style={{ color: "var(--muted)" }}>{relTime(it.check_in_at)}</span>
              </div>
              {it.prayer_request && (
                <p className="text-xs mt-0.5 pl-2 border-l-2 leading-snug" style={{ color: "var(--ink-2)", borderColor: "var(--terracotta)" }} data-testid="prayer-recent-note">
                  &ldquo;{it.prayer_request}&rdquo;
                </p>
              )}
            </li>
          ))}
        </ul>
      )}
      {items.length > 8 && (
        <p className="text-[0.65rem] mt-2" style={{ color: "var(--muted)" }}>+{items.length - 8} more</p>
      )}
    </div>
  );
}

function LookupStep({ onFound, onBack }) {
  const [last4, setLast4] = useState("");
  const [busy, setBusy] = useState(false);
  const { code } = useParams();
  const submit = async (e) => {
    e.preventDefault();
    if (last4.replace(/\D/g, "").length < 4) { toast.error("Please enter 4 digits."); return; }
    setBusy(true);
    try {
      const r = await api.post(`/public/prayer/${code}/lookup`, { last4 });
      const matches = r.data.matches || [];
      if (matches.length === 0) { toast.error("No one found with that number. Ask a leader to add you."); return; }
      onFound(matches);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong.");
    } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="text-center" data-testid="prayer-lookup-form">
      <button type="button" onClick={onBack} className="flex items-center gap-1 text-xs mb-4" style={{ color: "var(--muted)" }} data-testid="prayer-lookup-back">
        <ChevronLeft size={12} /> Back
      </button>
      <p className="text-sm mb-4" style={{ color: "var(--ink-2)" }}>Enter the last 4 digits of your phone number.</p>
      <input
        inputMode="numeric" pattern="[0-9]*" maxLength={6}
        value={last4} onChange={(e) => setLast4(e.target.value)}
        className="w-40 mx-auto text-center text-2xl tracking-[0.4em] py-3"
        data-testid="prayer-lookup-last4"
        autoFocus
      />
      <button type="submit" disabled={busy} className="btn-primary mt-6 w-full max-w-xs" data-testid="prayer-lookup-submit">
        {busy ? "Finding\u2026" : "Find me"}
      </button>
    </form>
  );
}

function ChooseStep({ matches, onPick, onBack }) {
  return (
    <div className="text-center" data-testid="prayer-choose">
      <button type="button" onClick={onBack} className="flex items-center gap-1 text-xs mb-4 mx-auto" style={{ color: "var(--muted)" }}>
        <ChevronLeft size={12} /> Back
      </button>
      <p className="text-sm mb-4" style={{ color: "var(--ink-2)" }}>{matches.length === 1 ? "Is this you?" : "Which one is you?"}</p>
      <div className="space-y-2">
        {matches.map((m) => (
          <button key={m.id} onClick={() => onPick(m)} className="w-full py-3 px-4 text-left rounded-md hover:bg-[var(--surface-hover)]" style={{ border: "1px solid var(--border-subtle)" }} data-testid={`prayer-pick-${m.id}`}>
            <div className="font-medium" style={{ color: "var(--ink)" }}>{formatDisplayName(m)}</div>
            <div className="text-xs" style={{ color: "var(--muted)" }}>{m.phone_hint}</div>
          </button>
        ))}
      </div>
    </div>
  );
}

// Optional prayer-request note the member can attach before checking in.
// Kept intentionally simple: a textarea + short character counter. Note is
// visible on the public 24-hour recent panel, so we warn about that.
// If the picked person is a household guardian, we also fetch their family so
// the parent can log dependents alongside themselves.
function ConfirmStep({ picked, request, setRequest, onSubmit, onBack, busy, code, familyIds, setFamilyIds }) {
  const remaining = 280 - request.length;
  const [family, setFamily] = useState(null); // null=loading, [] = none, [...] = list
  useEffect(() => {
    if (!picked) return;
    api.post(`/public/prayer/${code}/household`, { guest_id: picked.id })
      .then((r) => {
        setFamily(r.data.members || []);
        // Start un-checked — parent taps to add anyone praying with them.
        setFamilyIds([]);
      })
      .catch(() => setFamily([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [picked, code]);

  const toggle = (id) => {
    setFamilyIds(familyIds.includes(id) ? familyIds.filter((x) => x !== id) : [...familyIds, id]);
  };

  return (
    <div className="text-center" data-testid="prayer-confirm">
      <button type="button" onClick={onBack} className="flex items-center gap-1 text-xs mb-4 mx-auto" style={{ color: "var(--muted)" }}>
        <ChevronLeft size={12} /> Back
      </button>
      <p className="font-serif-display text-2xl mb-1" style={{ color: "var(--ink)" }}>Ready, {formatDisplayName(picked, { firstOnly: true })}?</p>

      {family && family.length > 0 && (
        <div className="mb-5 p-4 rounded-md text-left" style={{ background: "var(--surface-hover)", border: "1px solid var(--border-subtle)" }} data-testid="prayer-family">
          <div className="flex items-center gap-2 mb-3">
            <Users size={18} style={{ color: "var(--terracotta)" }} />
            <span className="text-base font-medium" style={{ color: "var(--ink)" }}>Anyone praying with you?</span>
          </div>
          <p className="text-sm mb-3" style={{ color: "var(--muted)" }}>Tap anyone who's here with you now.</p>
          <div className="space-y-2">
            {family.map((m) => {
              const checked = familyIds.includes(m.id);
              return (
                <label
                  key={m.id}
                  className="flex items-center gap-3 cursor-pointer rounded-md py-3 px-3 transition-colors"
                  style={{
                    background: checked ? "rgba(199, 108, 76, 0.12)" : "var(--bg-alt)",
                    border: `1px solid ${checked ? "var(--terracotta)" : "var(--border-subtle)"}`,
                    minHeight: "56px",
                  }}
                  data-testid={`prayer-family-toggle-${m.id}`}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggle(m.id)}
                    className="h-6 w-6 flex-shrink-0"
                    style={{ accentColor: "var(--terracotta)" }}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-lg font-medium truncate" style={{ color: "var(--ink)" }}>
                      {formatDisplayName(m)}
                    </div>
                    {m.age_group && (
                      <div className="text-xs" style={{ color: "var(--muted)" }}>{m.age_group}</div>
                    )}
                  </div>
                </label>
              );
            })}
          </div>
        </div>
      )}

      <p className="text-xs mb-4" style={{ color: "var(--muted)" }}>Anything specific you&rsquo;re praying about? <span className="italic">(optional)</span></p>
      <textarea
        rows={4}
        maxLength={280}
        value={request}
        onChange={(e) => setRequest(e.target.value)}
        placeholder="A short prayer request or Scripture on your heart…"
        className="w-full px-3 py-2 text-sm text-left"
        data-testid="prayer-request-input"
      />
      <div className="text-[0.65rem] mt-1 flex items-center justify-between" style={{ color: "var(--muted)" }}>
        <span>Visible to others who check in over the next 24 hours.</span>
        <span>{remaining}</span>
      </div>
      <button
        onClick={onSubmit}
        disabled={busy}
        className="w-full py-4 mt-4 rounded-md font-serif-display text-lg"
        style={{ background: "var(--terracotta)", color: "#FFFFFF" }}
        data-testid="prayer-confirm-submit"
      >
        {busy ? "Recording\u2026" : (familyIds.length > 0 ? `Check in ${familyIds.length + 1} of us for prayer` : "Check in for prayer")}
      </button>
    </div>
  );
}

function DupWarning({ lastAt, onConfirm, onCancel }) {
  return (
    <div className="text-center" data-testid="prayer-dup-warning">
      <div className="w-14 h-14 mx-auto rounded-full inline-flex items-center justify-center mb-4" style={{ background: "var(--surface-hover)", color: "var(--terracotta)" }}>
        <Clock size={24} />
      </div>
      <p className="font-serif-display text-2xl mb-2" style={{ color: "var(--ink)" }}>Already checked in</p>
      <p className="text-sm" style={{ color: "var(--ink-2)" }}>You checked in for prayer at <strong>{formatDate(lastAt)}</strong>.</p>
      <div className="flex flex-col sm:flex-row gap-2 mt-6">
        <button onClick={onCancel} className="btn-ghost flex-1" data-testid="prayer-dup-done">Done</button>
        <button onClick={onConfirm} className="btn-primary flex-1" data-testid="prayer-dup-again">Check in again</button>
      </div>
    </div>
  );
}

export default function PrayerPublic() {
  useKioskIdleReturn();
  const { code } = useParams();
  const [orgName, setOrgName] = useState("");
  const [loadErr, setLoadErr] = useState("");
  const [step, setStep] = useState("welcome"); // welcome | lookup | choose | confirm | duplicate | success
  const [matches, setMatches] = useState([]);
  const [picked, setPicked] = useState(null);
  const [request, setRequest] = useState("");
  const [busy, setBusy] = useState(false);
  const [dupInfo, setDupInfo] = useState(null);
  const [successAt, setSuccessAt] = useState(null);
  const [recentTick, setRecentTick] = useState(0);
  const [familyIds, setFamilyIds] = useState([]);
  const [successExtras, setSuccessExtras] = useState([]);

  useEffect(() => {
    api.get(`/public/prayer/${code}`)
      .then((r) => setOrgName(r.data.organization?.name || ""))
      .catch((e) => setLoadErr(e?.response?.data?.detail || "This prayer QR isn't recognized."));
  }, [code]);

  const reset = useCallback(() => {
    setStep("welcome"); setMatches([]); setPicked(null); setRequest(""); setDupInfo(null); setSuccessAt(null); setFamilyIds([]); setSuccessExtras([]);
  }, []);

  // Auto-reset after successful check-in (kiosk safety — don't leave prior member's info on screen)
  useEffect(() => {
    if (step !== "success") return;
    const t = setTimeout(reset, 8000);
    return () => clearTimeout(t);
  }, [step, reset]);

  const doCheckin = useCallback(async (guest, force = false, note = "", extraIds = []) => {
    setBusy(true);
    try {
      const r = await api.post(`/public/prayer/${code}/checkin`, { guest_id: guest.id, force, prayer_request: note, additional_guest_ids: extraIds });
      setPicked(guest);
      setSuccessAt(r.data.check_in_at);
      setSuccessExtras(r.data.additional_marked || []);
      setRecentTick((t) => t + 1);
      setStep("success");
    } catch (err) {
      const d = err?.response?.data?.detail;
      if (err?.response?.status === 409 && d && d.duplicate) {
        setPicked(guest);
        setDupInfo(d);
        setStep("duplicate");
      } else {
        toast.error(typeof d === "string" ? d : "Couldn't record your check-in.");
      }
    } finally { setBusy(false); }
  }, [code]);

  if (loadErr) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4" style={{ background: "var(--bg-base)" }}>
        <div className="max-w-sm text-center" data-testid="prayer-invalid">
          <h2 className="font-serif-display text-2xl mb-2" style={{ color: "var(--ink)" }}>QR not recognized</h2>
          <p className="text-sm" style={{ color: "var(--ink-2)" }}>{loadErr}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-start sm:items-center justify-center px-4 py-10" style={{ background: "var(--bg-base)" }}>
      <div className="w-full max-w-md">
        <OrgHeader orgName={orgName} />

        <div className="card-surface p-6 sm:p-8" style={{ background: "var(--bg-alt)" }}>
          {step === "welcome" && (
            <div className="text-center" data-testid="prayer-welcome">
              <p className="text-sm mb-8 leading-relaxed" style={{ color: "var(--ink-2)" }}>
                Thank you for making prayer a priority.
              </p>
              <button
                onClick={() => setStep("lookup")}
                className="w-full py-6 rounded-md font-serif-display text-xl transition-colors"
                style={{ background: "var(--terracotta)", color: "#FFFFFF" }}
                data-testid="prayer-checkin-btn"
              >
                <Heart className="inline mr-2" size={20} /> Check in for prayer
              </button>
              <p className="text-[0.7rem] mt-4" style={{ color: "var(--muted)" }}>
                We&rsquo;ll ask for the last 4 digits of your phone number to find you.
              </p>
            </div>
          )}

          {step === "lookup" && (
            <LookupStep onFound={(m) => { setMatches(m); setStep(m.length === 1 ? "choose" : "choose"); }} onBack={reset} />
          )}

          {step === "choose" && (
            <ChooseStep matches={matches} onPick={(m) => { setPicked(m); setStep("confirm"); }} onBack={() => setStep("lookup")} />
          )}

          {step === "confirm" && picked && (
            <ConfirmStep
              picked={picked}
              request={request}
              setRequest={setRequest}
              busy={busy}
              code={code}
              familyIds={familyIds}
              setFamilyIds={setFamilyIds}
              onSubmit={() => doCheckin(picked, false, request, familyIds)}
              onBack={() => setStep(matches.length > 1 ? "choose" : "lookup")}
            />
          )}

          {step === "duplicate" && dupInfo && picked && (
            <DupWarning
              lastAt={dupInfo.last_check_in_at}
              onConfirm={() => doCheckin(picked, true, request, familyIds)}
              onCancel={reset}
            />
          )}

          {step === "success" && picked && (
            <div className="text-center" data-testid="prayer-success">
              <div className="w-14 h-14 mx-auto rounded-full inline-flex items-center justify-center mb-4" style={{ background: "var(--olive)", color: "#FFF" }}>
                <CheckCircle2 size={28} />
              </div>
              <p className="font-serif-display text-2xl mb-2" style={{ color: "var(--ink)" }}>Prayer check-in recorded</p>
              <p className="text-sm" style={{ color: "var(--ink-2)" }}>
                <strong>{formatDisplayName(picked, { firstOnly: true })}</strong> &mdash; thank you for making prayer a priority.
              </p>
              {successExtras.length > 0 && (
                <p className="text-xs mt-2" style={{ color: "var(--ink-2)" }} data-testid="prayer-success-extras">
                  Also logged: {successExtras.map((e) => formatDisplayName(e)).filter(Boolean).join(" · ")}
                </p>
              )}
              <p className="text-xs mt-2" style={{ color: "var(--muted)" }}>{formatDate(successAt)}</p>
              <button onClick={reset} className="btn-ghost mt-6 text-xs" data-testid="prayer-success-done">Done</button>
            </div>
          )}
        </div>

        <RecentPanel code={code} refreshTick={recentTick} />

        <p className="text-center text-[0.65rem] mt-6" style={{ color: "var(--muted)" }}>
          <Sparkles size={10} className="inline mr-1" /> thedisciple.prayer &middot; A church-resources tool
        </p>
      </div>
    </div>
  );
}
